import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useActiveCompany } from "@/hooks/use-active-company";
import { usePricingRole } from "@/hooks/use-pricing-role";
import { formatCurrency, formatDate } from "@/lib/format";

export const Route = createFileRoute("/_authenticated/invoices/$id_/purring")({
  head: () => ({ meta: [
    { title: "Purring | VERQO" },
    { name: "description", content: "Purrebrev med forfallsdato, forsinkelsesrente og purregebyr." },
    { property: "og:title", content: "Purring | VERQO" },
    { property: "og:description", content: "Purrebrev med forfallsdato, forsinkelsesrente og purregebyr." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
  ] }),
  component: ReminderLetter,
});

const DAY = 86_400_000;

function ReminderLetter() {
  const { id } = Route.useParams();
  const { companyId } = useActiveCompany();
  const qc = useQueryClient();
  const { canManagePricing, isLoading: roleLoading } = usePricingRole();

  const query = useQuery({
    queryKey: ["invoice-reminder", id],
    enabled: Boolean(companyId) && canManagePricing,
    queryFn: async () => {
      const [{ data: invoice, error }, { data: balance, error: balanceError }, { data: snapshot, error: settingsError }, { data: reminder }] = await Promise.all([
        supabase.from("invoice_document_header_v").select("invoice_id,invoice_number,document_type,credit_of_invoice_id,title,description,presentation,reference_name,issue_date,issued_at,delivery_date,due_date,payment_terms_days,subtotal_excl_vat,vat_amount,total_incl_vat,vat_percent,bill_to_name,bill_to_org_number,bill_to_address,bill_to_postal_code,bill_to_city,site_address,site_postal_code,site_city,site_contact_name,site_contact_phone,terms_payment,terms_included,terms_excluded,terms_reservations,seller_name,seller_org_number,seller_address,seller_postal_code,seller_city,seller_phone,seller_email,seller_bank_account,seller_iban,seller_vat_registered,seller_logo_storage_path").eq("invoice_id", id).single(),
        supabase.from("invoice_balance_v").select("paid_net,net_outstanding").eq("invoice_id", id).maybeSingle(),
        supabase.from("invoices").select("seller_late_interest_percent,seller_reminder_fee").eq("id", id).single(),
        supabase.from("invoice_reminders").select("reminder_no,calculated_at,outstanding_base,interest_rate_used,interest_amount,fee_used,due_date").eq("invoice_id", id).order("reminder_no", { ascending: false }).limit(1).maybeSingle(),
      ]);
      if (error) throw error;
      if (balanceError) throw balanceError;
      if (settingsError) throw settingsError;
      return { invoice, paid: Number(balance?.paid_net ?? 0), outstanding: Number(balance?.net_outstanding ?? 0), snapshot, reminder };
    },
  });

  const register = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc("register_invoice_reminder", { _invoice_id: id });
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["invoice-reminder", id] }); toast.success("Purringen er lagret med dagens rente og gebyr"); },
    onError: (error: Error) => toast.error(error.message),
  });

  if (roleLoading) return <div className="p-10 text-center">Laster…</div>;
  if (!canManagePricing) return <div className="p-10 text-center">Ingen tilgang.</div>;
  if (!query.data) return <div className="p-10 text-center">Laster…</div>;
  const { invoice, paid, outstanding, snapshot, reminder } = query.data;

  const today = new Date();
  const due = invoice.due_date ? new Date(invoice.due_date) : null;
  const daysOverdue = due ? Math.max(0, Math.floor((today.getTime() - due.getTime()) / DAY)) : 0;
  // Satsene er frosset på bilaget ved utstedelse. Er purringen registrert, brukes de lagrede tallene.
  const interestPercent = Number(reminder?.interest_rate_used ?? snapshot.seller_late_interest_percent ?? 0);
  const base = Number(reminder?.outstanding_base ?? outstanding);
  const interest = reminder ? Number(reminder.interest_amount) : base * (interestPercent / 100) * (daysOverdue / 365);
  const fee = Number(reminder?.fee_used ?? snapshot.seller_reminder_fee ?? 0);
  const total = base + interest + fee;

  return <>
    <style>{`@page{size:A4;margin:18mm}@media print{.no-print{display:none!important}body{background:white!important}}`}</style>
    <div className="no-print sticky top-0 flex justify-between border-b bg-card p-3">
      <Button asChild variant="ghost"><Link to="/invoices/$id" params={{ id }}><ArrowLeft className="h-4 w-4"/>Tilbake</Link></Button>
      <div className="flex gap-2">
        {!reminder && <Button variant="outline" onClick={() => register.mutate()} disabled={register.isPending}>Registrer purring</Button>}
        <Button onClick={() => window.print()}><Printer className="h-4 w-4"/>Skriv ut / lagre PDF</Button>
      </div>
    </div>
    <article className="mx-auto my-8 max-w-[210mm] bg-white p-12 text-neutral-900 shadow-xl print:my-0 print:shadow-none">
      <header className="flex justify-between gap-8 border-b-2 border-neutral-900 pb-8">
        <div>
          {invoice.seller_logo_storage_path?.startsWith("http") && <img src={invoice.seller_logo_storage_path} alt={invoice.seller_name ?? ""} className="mb-4 max-h-20 max-w-52 object-contain"/>}
          <div className="font-semibold">{invoice.seller_name}</div>
          <div className="text-sm text-neutral-600">Org.nr {invoice.seller_org_number}{invoice.seller_vat_registered ? " MVA" : ""}</div>
          <div className="text-sm text-neutral-600">{invoice.seller_address}</div>
          <div className="text-sm text-neutral-600">{invoice.seller_postal_code} {invoice.seller_city}</div>
          <div className="text-sm text-neutral-600">Telefon: {invoice.seller_phone}</div>
          <div className="text-sm text-neutral-600">{invoice.seller_email}</div>
        </div>
        <div className="text-right">
          <h1 className="text-3xl font-semibold">PURRING</h1>
          <div className="mt-3 text-sm">Gjelder faktura: <b>{invoice.invoice_number}</b></div>
          <div className="text-sm">Fakturadato: {formatDate(invoice.issue_date)}</div>
          <div className="text-sm">Opprinnelig forfall: {formatDate(invoice.due_date)}</div>
          <div className="text-sm">Dato: {formatDate(today.toISOString().slice(0, 10))}</div>
        </div>
      </header>
      <section className="py-8">
        <div className="mb-2 text-xs font-semibold uppercase text-neutral-500">Til</div>
        <div className="font-semibold">{invoice.bill_to_name}</div>
        {invoice.bill_to_org_number && <div className="text-sm">Org.nr {invoice.bill_to_org_number}</div>}
        <div className="text-sm">{invoice.bill_to_address}</div>
        <div className="text-sm">{invoice.bill_to_postal_code} {invoice.bill_to_city}</div>
      </section>
      <p className="text-sm">
        Vi kan ikke se at faktura {invoice.invoice_number} med forfallsdato {formatDate(invoice.due_date)} er betalt.
        Fakturaen er {daysOverdue} dager over forfall. Vi ber om at beløpet betales snarest.
      </p>
      <section className="mt-8 w-96 text-sm">
        <Row label="Fakturabeløp" value={formatCurrency(Number(invoice.total_incl_vat))}/>
        {paid > 0 && <Row label="Innbetalt" value={`− ${formatCurrency(paid)}`}/>}
        <Row label="Utestående" value={formatCurrency(base)}/>
        {interestPercent > 0 && <Row label={`Forsinkelsesrente (${interestPercent} % p.a., ${daysOverdue} dager)`} value={formatCurrency(interest)}/>}
        {fee > 0 && <Row label="Purregebyr" value={formatCurrency(fee)}/>}
        <div className="my-2 border-t border-neutral-900"/>
        <div className="flex justify-between py-1 text-lg font-semibold"><span>Å betale nå</span><span>{formatCurrency(total)}</span></div>
      </section>
      <section className="mt-10 border-t pt-6 text-sm">
        <div className="mb-2 text-xs font-semibold uppercase text-neutral-500">Betaling</div>
        <div>Kontonummer: <b>{invoice.seller_bank_account || "Ikke oppgitt"}</b>{invoice.seller_iban ? ` · IBAN ${invoice.seller_iban}` : ""}</div>
        <div>Merk betalingen med fakturanummer {invoice.invoice_number}.</div>
        <p className="mt-3 text-xs text-neutral-600">Er beløpet betalt i mellomtiden, kan du se bort fra denne purringen.</p>
      </section>
    </article>
  </>;
}

function Row({ label, value }: { label: string; value: string }) {
  return <div className="flex justify-between py-1"><span>{label}</span><span>{value}</span></div>;
}
